import { useEffect, useRef, useState, type ReactNode } from "react";
import { cx } from "@/lib/cx";

interface ChipOption<T extends string> {
  value: T;
  label: string;
  badge?: number;
}

interface ChipsProps<T extends string> {
  options: ChipOption<T>[];
  value: T;
  onChange: (value: T) => void;
  ariaLabel: string;
  className?: string;
  trailing?: ReactNode;
}

export function Chips<T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
  className,
  trailing,
}: ChipsProps<T>) {
  const navRef = useRef<HTMLElement | null>(null);
  const activeRef = useRef<HTMLButtonElement | null>(null);
  const [edges, setEdges] = useState({ start: false, end: false });

  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;
    const update = () => {
      const max = nav.scrollWidth - nav.clientWidth;
      setEdges({
        start: nav.scrollLeft > 2,
        end: max > 2 && nav.scrollLeft < max - 2,
      });
    };
    update();
    nav.addEventListener("scroll", update, { passive: true });
    const observer = new ResizeObserver(update);
    observer.observe(nav);
    return () => {
      nav.removeEventListener("scroll", update);
      observer.disconnect();
    };
  }, [options.length]);

  useEffect(() => {
    activeRef.current?.scrollIntoView({ inline: "center", block: "nearest", behavior: "smooth" });
  }, [value]);

  return (
    <div className={cx("chips-wrap", edges.start && "at-start", edges.end && "at-end")}>
      <nav ref={navRef} className={cx("chips", className)} aria-label={ariaLabel}>
        {options.map((option) => (
          <button
            key={option.value}
            ref={value === option.value ? activeRef : undefined}
            type="button"
            className={cx("chip", value === option.value && "active")}
            aria-pressed={value === option.value}
            onClick={() => onChange(option.value)}
          >
            {option.label}
            {typeof option.badge === "number" && option.badge > 0 ? (
              <span className="ml-[6px] font-bold" aria-hidden="true">
                {option.badge}
              </span>
            ) : null}
          </button>
        ))}
        {trailing}
      </nav>
    </div>
  );
}
